import React, { useState, useEffect, useRef } from "react";
import { emailAPI, authAPI, templateAPI, logsAPI, apiUtils } from "../services/apiService.js";
import graphAPI, { initializeMSAL } from "../services/graphService.js";
import i18n from "../../i18n/i18n.js";
import config from "../config.js";
import AccessDeniedScreen from "./screens/AccessDeniedScreen.jsx";
import AdminScreen from "./screens/AdminScreen.jsx";
import MobileReportScreen from "./screens/MobileReportScreen.jsx";

export default function App() {
  const [activeUserEmail, setActiveUserEmail] = useState(null);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState(i18n.getLanguage());

  // Permission and user state
  const [hasPermission, setHasPermission] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [permissionError, setPermissionError] = useState(null);

  // Platform detection
  const [isMobile, setIsMobile] = useState(false);

  // Email report state (for mobile)
  const [reportType, setReportType] = useState(null); // "spam", "phishing", "suspicious"
  const [reportAdditionalInfo, setReportAdditionalInfo] = useState("");
  const [reportingEmail, setReportingEmail] = useState(false);

  // Admin configuration state
  const [adminSettings, setAdminSettings] = useState({
    allowMove: false,
    allowDelete: false,
    forwardEmail: "",
    forwardPhishingToSoc: true,
    emailSubject: "",
    emailBody: "",
    allowUserFeedback: false,
    endpointUrl: "",
  });

  // User feedback state
  const textareaRef = useRef(null);

  // Tab state
  const [activeTab, setActiveTab] = useState("settings");

  // Logs state
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logsError, setLogsError] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);

  // Awareness message flow state
  const [flowStep, setFlowStep] = useState("template"); // template, group, confirm, success
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedGroups, setSelectedGroups] = useState([]); // Array of selected groups
  const [manualEmails, setManualEmails] = useState(""); // Comma or newline separated email addresses
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Template placeholder editing state
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [placeholderValues, setPlaceholderValues] = useState({});
  const [logoUrls, setLogoUrls] = useState({}); // Store logo URLs by placeholder name (e.g., LOGO_URL_1, LOGO_URL_2)
  const [editingField, setEditingField] = useState(null); // Track which field is being edited (subject, body, or null)
  const [editedHtmlContent, setEditedHtmlContent] = useState(null); // Store edited HTML content from preview
  const [editingLogoPlaceholder, setEditingLogoPlaceholder] = useState(null); // Track which logo is being edited
  const [changesSaved, setChangesSaved] = useState(true); // Track if current changes have been saved
  const previewRef = useRef(null); // Ref for preview container

  // Template state - loaded from API
  const [templates, setTemplates] = useState([]);

  // Load templates from API (with user-specific customizations)
  const loadTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const response = await templateAPI.getTemplates(activeUserEmail);
      // Handle different response formats (array or object with data property)
      let templatesData = Array.isArray(response)
        ? response
        : response.data || response.templates || [];

      // Normalize template data: convert _id to id for consistency and preserve all properties
      templatesData = templatesData.map((template) => {
        const normalized = {
          ...template,
          id: template._id || template.id, // Use _id if present, otherwise use id
          _id: template._id || template.id, // Keep _id for compatibility
          subject: template.subject || "",
          htmlBody: template.htmlBody || "",
          isCustomized: template.isCustomized || false,
          customData: template.customData || {},
        };
        return normalized;
      });
      setTemplates(templatesData);
    } catch (error) {
      // console.error("Error loading templates from API:", error);
      showMessage(
        apiUtils.formatErrorMessage(error) || i18n.t("home.failedToLoadTemplates"),
        "error"
      );
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Initialize templates on mount
  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload templates when activeUserEmail becomes available (to get user-specific customizations)
  useEffect(() => {
    if (activeUserEmail) {
      loadTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUserEmail]);

  // Setup event handlers for template preview when template is selected
  useEffect(() => {
    if (!selectedTemplate || !previewRef.current) return;

    const el = previewRef.current;
    let cleanupFunctions = [];
    let observer = null;

    // Function to setup event handlers
    const setupHandlers = () => {
      // Clean up existing handlers first
      cleanupFunctions.forEach((cleanup) => cleanup());
      cleanupFunctions = [];

      // Setup logo click handlers
      const logos = el.querySelectorAll('img[data-editable="logo"]');
      logos.forEach((logo) => {
        logo.style.cursor = "pointer";
        logo.style.border = "2px dashed transparent";
        logo.style.transition = "border 0.2s";

        const handleMouseEnter = () => {
          logo.style.border = "2px dashed #0C75D1";
          logo.style.backgroundColor = "rgba(12, 117, 209, 0.1)";
        };

        const handleMouseLeave = () => {
          logo.style.border = "2px dashed transparent";
          logo.style.backgroundColor = "transparent";
        };

        const handleClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          const placeholder = logo.getAttribute("data-logo-placeholder");
          if (placeholder) {
            setEditingLogoPlaceholder(placeholder);
          }
        };

        logo.addEventListener("mouseenter", handleMouseEnter);
        logo.addEventListener("mouseleave", handleMouseLeave);
        logo.addEventListener("click", handleClick);

        cleanupFunctions.push(() => {
          logo.removeEventListener("mouseenter", handleMouseEnter);
          logo.removeEventListener("mouseleave", handleMouseLeave);
          logo.removeEventListener("click", handleClick);
        });
      });

      // Make editable areas clickable
      const editableAreas = el.querySelectorAll('[data-editable="text"]');
      editableAreas.forEach((area) => {
        area.style.cursor = "text";
        area.style.outline = "none";
        area.contentEditable = true;
        area.suppressContentEditableWarning = true;

        const handleFocus = (e) => {
          e.stopPropagation();
          area.style.backgroundColor = "#f0f9ff";
          area.style.border = "1px dashed #0C75D1";
          area.style.padding = "2px";
          area.style.borderRadius = "2px";
        };

        const handleBlur = (e) => {
          e.stopPropagation();
          area.style.backgroundColor = "transparent";
          area.style.border = "none";
          area.style.padding = "0";
          // Save edited content
          if (el) {
            const htmlContent = el.innerHTML;
            setEditedHtmlContent((prev) => {
              const currentBody = prev?.htmlBody || selectedTemplate?.htmlBody || "";
              if (htmlContent !== currentBody) {
                setChangesSaved(false); // Mark as unsaved when content changes
              }
              return {
                ...prev,
                htmlBody: htmlContent,
              };
            });
          }
        };

        const handleClick = (e) => {
          e.stopPropagation();
        };

        const handleInput = (e) => {
          e.stopPropagation();
          // Mark as unsaved on input
          setChangesSaved(false);
        };

        area.addEventListener("focus", handleFocus);
        area.addEventListener("blur", handleBlur);
        area.addEventListener("click", handleClick);
        area.addEventListener("input", handleInput);

        cleanupFunctions.push(() => {
          area.removeEventListener("focus", handleFocus);
          area.removeEventListener("blur", handleBlur);
          area.removeEventListener("click", handleClick);
          area.removeEventListener("input", handleInput);
        });
      });
    };

    // Setup handlers after DOM is ready - use longer delay to ensure dangerouslySetInnerHTML has updated
    const timeoutId = setTimeout(() => {
      setupHandlers();

      // Also setup MutationObserver to re-initialize handlers when DOM changes
      observer = new MutationObserver((mutations) => {
        // Only re-setup if child nodes were added/removed (not just attribute changes)
        const hasStructuralChanges = mutations.some(
          (m) => m.type === "childList" && (m.addedNodes.length > 0 || m.removedNodes.length > 0)
        );
        if (hasStructuralChanges) {
          // Small delay to ensure DOM is stable
          setTimeout(setupHandlers, 100);
        }
      });

      observer.observe(el, {
        childList: true,
        subtree: true,
      });
    }, 300);

    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
      if (observer) {
        observer.disconnect();
      }
      cleanupFunctions.forEach((cleanup) => cleanup());
      cleanupFunctions = [];
    };
  }, [selectedTemplate, editedHtmlContent, logoUrls]);

  // Load logs when logs tab is active
  const loadLogs = async () => {
    if (activeTab !== "logs") return;

    try {
      setLoadingLogs(true);
      setLogsError(null);

      // Get domain from organization or user email
      const domain = organization?.name || (activeUserEmail ? activeUserEmail.split("@")[1] : null);

      if (!domain) {
        setLogsError("Domain not found");
        return;
      }

      const response = await logsAPI.getLogs({
        domain: domain.toLowerCase(),
        limit: 50,
        page: 1,
      });

      if (response.success) {
        setLogs(response.data || []);
      } else {
        setLogsError(response.error || "Failed to load logs");
      }
    } catch (error) {
      // console.error("Error loading logs:", error);
      setLogsError(apiUtils.formatErrorMessage(error) || "Failed to load logs");
    } finally {
      setLoadingLogs(false);
    }
  };

  useEffect(() => {
    if (activeTab === "logs" && isAdmin) {
      loadLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isAdmin, organization, activeUserEmail]);

  useEffect(() => {
    initializeApp();
    // Initialize MSAL for Graph API
    initializeMSAL().catch((error) => {
      // console.error("Failed to initialize MSAL:", error);
    });
  }, []);

  // Get text direction based on language
  const getTextDirection = () => {
    const lang = i18n.getLanguage();
    return lang.startsWith("ar") ? "rtl" : "ltr";
  };

  // Update document direction when language changes
  useEffect(() => {
    const dir = getTextDirection();
    document.documentElement.setAttribute("dir", dir);
    document.documentElement.setAttribute("lang", i18n.getLanguage());

    // Also update body class for RTL/LTR styling
    document.body.classList.remove("rtl", "ltr");
    document.body.classList.add(dir);
  }, [currentLanguage]);

  // Load admin settings when userInfo changes
  useEffect(() => {
    if (userInfo && isAdmin) {
      loadAdminSettings();
    }
  }, [userInfo, isAdmin]);

  const initializeApp = async () => {
    try {
      // Ensure Office is ready before accessing Office.context
      await new Promise((resolve) => {
        if (typeof Office !== "undefined" && Office.onReady) {
          Office.onReady(() => resolve());
        } else {
          resolve();
        }
      });

      // Access current mailbox item if needed
      // const email = Office.context.userProfile.email;
      // const domain = email.substring(email.indexOf("@") + 1);
      // console.log(domain); // e.g., "contoso.com"

      // Initialize i18n language based on Office.context.displayLanguage
      // This should be done first to ensure all UI text is in the correct language
      if (typeof Office !== "undefined" && Office.context) {
        i18n.initializeLanguage();
        const detectedLang = i18n.getLanguage();
        setCurrentLanguage(detectedLang); // Trigger re-render with new language
        // console.log("App initialized with language:", detectedLang);
        // console.log("Office display language:", Office.context.displayLanguage);
      }

      // Get user email from Office context
      const userProfile = Office.context.mailbox.userProfile;
      // console.log("User profile:", userProfile);
      // console.log(Office.context.mailbox.item);
      // console.log(Office.context.mailbox.item.to[0].emailAddress);

      // setActiveUserEmail(userProfile.emailAddress);
      // setActiveUserEmail(Office.context.mailbox.item.to[0].emailAddress);
      setActiveUserEmail(userProfile.emailAddress);

      // check platform
      const platform = Office.context.platform;
      // const platformType = Office.PlatformType;
      // console.log("Platform:", platform);
      // console.log("PlatformType",platformType);

      // Detect mobile platforms
      // Office.context.platform returns "Mobile" for Android and iOS devices
      // For web/desktop, it returns "OfficeOnline", "Mac", "PC", etc.
      const isMobilePlatform = platform === "Android" || platform === "iOS";

      // console.log("Is Mobile Platform:", isMobilePlatform);
      setIsMobile(isMobilePlatform);

      // if (platform === "OfficeOnline") {
      //   console.log("Web platform detected");
      // } else if (isMobilePlatform) {
      //   console.log("Mobile platform detected");
      // }

      // Check user permission first
      await checkUserPermission(userProfile.emailAddress);
    } catch (error) {
      // console.error("Initialization error:", error);
      showMessage(i18n.t("messages.initializationFailed"), "error");
    }
  };

  const checkUserPermission = async (email) => {
    try {
      setLoading(true);
      const response = await authAPI.checkUserPermission(email.toLowerCase());

      // console.log("Permission check response:", response);

      if (response.success && response.hasPermission) {
        // console.log("Permission check response:", response);
        setHasPermission(true);
        setPermissionError(null); // Clear any previous errors
        setUserInfo(response.user);
        setOrganization(response.organization);
        setIsAdmin(response.isAdmin);

        // For admins, always show admin configuration screen
        if (response.isAdmin) {
          // Set userInfo, settings will be loaded via useEffect
          if (response.user) {
            setUserInfo(response.user);
          }
        }
      } else {
        // Handle different types of permission errors
        setHasPermission(false);

        if (response.error && response.error.includes("No administrator has configured")) {
          // User exists but no admin configured - show different message
          setPermissionError("no_admin_configured");
          showMessage(i18n.t("messages.noAdminConfigured"), "warning");
        } else if (response.error && response.error.includes("User not found")) {
          // User doesn't exist in database
          setPermissionError("user_not_found");
          showMessage(i18n.t("messages.accessDenied"), "error");
        } else {
          // Other errors
          setPermissionError("other");
          showMessage(response.error || i18n.t("messages.accessDenied"), "error");
        }
      }
    } catch (error) {
      // console.error("Permission check error:", error);
      setHasPermission(false);
      showMessage(apiUtils.formatErrorMessage(error), "error");
    } finally {
      setLoading(false);
    }
  };

  const loadAdminSettings = async () => {
    try {
      // Settings are now loaded from userInfo in checkUserPermission
      if (userInfo) {
        const emailBody = userInfo.emailBody || "";

        const move = !!(userInfo.allowedActions?.move);
        const del = !!(userInfo.allowedActions?.delete);
        setAdminSettings({
          allowMove: move && !del,
          allowDelete: del && !move,
          forwardEmail: userInfo.forwardEmail || "",
          forwardPhishingToSoc: userInfo.forwardPhishingToSoc !== false,
          emailSubject: userInfo.emailSubject || "",
          emailBody: emailBody,
          allowUserFeedback: userInfo.allowUserFeedback || false,
          endpointUrl: userInfo.endpointUrl || "",
        });
      }
    } catch (error) {
      // console.error("Error loading admin settings:", error);
    }
  };

  const handleSaveAdminSettings = async () => {
    try {
      setLoading(true);

      // Validate mandatory forward email
      if (!adminSettings.forwardEmail.trim()) {
        showMessage(i18n.t("messages.forwardEmailRequired"), "error");
        setLoading(false);
        return;
      }

      const syncData = {
        allowedActions: {
          move: adminSettings.allowMove,
          delete: adminSettings.allowDelete,
        },
        forwardEmail: adminSettings.forwardEmail,
        forwardPhishingToSoc: adminSettings.forwardPhishingToSoc,
        emailSubject: adminSettings.emailSubject,
        allowUserFeedback: adminSettings.allowUserFeedback,
        endpointUrl: adminSettings.endpointUrl,
      };

      syncData.emailBody = adminSettings.emailBody || "";

      const response = await authAPI.syncUserSettings(activeUserEmail, syncData);

      if (response.success) {
        showMessage(i18n.t("messages.settingsSaveSuccess"), "success");

        // Refresh user info and settings
        await checkUserPermission(activeUserEmail);
      }
    } catch (error) {
      // console.error("Save settings error:", error);
      showMessage(apiUtils.formatErrorMessage(error), "error");
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (text, type = "info") => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  const getMessageBarClass = (type) => {
    switch (type) {
      case "success":
        return "bg-green-50 border border-green-200 text-green-800";
      case "error":
        return "bg-red-50 border border-red-200 text-red-800";
      case "warning":
        return "bg-yellow-50 border border-yellow-200 text-yellow-800";
      default:
        return "bg-blue-50 border border-blue-200 text-blue-800";
    }
  };

  // Extract all placeholders from text
  // Note: DATE, TIME, YEAR, DATETIME are excluded as they're handled automatically by Outlook
  const extractPlaceholders = (text) => {
    if (!text) return [];
    const matches = text.match(/\{\{(\w+)\}\}/g);
    if (!matches) return [];
    // Get unique placeholders, excluding auto-handled ones
    const autoHandled = ["DATE", "TIME", "YEAR", "DATETIME"];
    const unique = [
      ...new Set(matches.map((m) => m.replace(/\{\{|\}\}/g, "").toUpperCase())),
    ].filter((p) => !autoHandled.includes(p));
    return unique;
  };

  // Placeholder substitution function
  const substitutePlaceholders = (text, variables = {}) => {
    if (!text) return text;

    // Default variables
    const defaultVars = {
      DATE: new Date().toLocaleDateString(),
      TIME: new Date().toLocaleTimeString(),
      DATETIME: new Date().toLocaleString(),
      YEAR: new Date().getFullYear().toString(),
      ORGANIZATION: organization?.name || "Our Organization",
      // For USER_NAME, never fall back to the raw email address.
      // If we don't have a display name, use a generic friendly label instead.
      USER_NAME: userInfo?.displayName || "There",
      USER_EMAIL: activeUserEmail || "user@example.com",
      GROUP_NAME: selectedGroups.map((g) => g.displayName).join(", ") || "Group",
      ...variables,
    };

    // Replace placeholders in format {{PLACEHOLDER}}
    return text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
      const upperKey = key.toUpperCase();
      // First check user-provided values, then defaults
      if (variables[upperKey] !== undefined) {
        return variables[upperKey];
      }
      return defaultVars[upperKey] !== undefined ? defaultVars[upperKey] : match; // Keep original if not found
    });
  };

  // Extract logo URL placeholders from template (e.g., {{LOGO_URL_1}}, {{LOGO_URL_2}})
  const extractLogoPlaceholders = (text) => {
    if (!text) return [];
    const matches = text.match(/\{\{LOGO_URL(_\d+)?\}\}/gi);
    if (!matches) return [];
    return [...new Set(matches.map((m) => m.replace(/\{\{|\}\}/gi, "").toUpperCase()))];
  };

  // Template placeholder editing handlers
  const handleEditTemplate = (template, field = null) => {
    setEditingTemplate(template);
    setEditingField(field); // field can be 'subject', 'body', or null for full edit

    // Extract all placeholders from subject and body
    const subjectPlaceholders = extractPlaceholders(template.subject);
    const bodyPlaceholders = extractPlaceholders(template.htmlBody);
    const allPlaceholders = [...new Set([...subjectPlaceholders, ...bodyPlaceholders])];

    // Extract logo URL placeholders
    const logoPlaceholders = [
      ...extractLogoPlaceholders(template.subject),
      ...extractLogoPlaceholders(template.htmlBody),
    ];

    // Initialize placeholder values - NO auto-fill, admin must write manually
    const initialValues = {};
    allPlaceholders.forEach((placeholder) => {
      // Don't auto-fill - start with empty string
      initialValues[placeholder] = "";
    });

    // Initialize logo URLs from customData if available
    const initialLogoUrls = {};
    if (template.customData && template.customData.logoUrls) {
      Object.assign(initialLogoUrls, template.customData.logoUrls);
    }
    logoPlaceholders.forEach((logoPlaceholder) => {
      if (!initialLogoUrls[logoPlaceholder]) {
        initialLogoUrls[logoPlaceholder] = "";
      }
    });

    setPlaceholderValues(initialValues);
    setLogoUrls(initialLogoUrls);
  };

  // Replace logo URL placeholders in text
  const replaceLogoPlaceholders = (text, logoUrls) => {
    if (!text) return text;
    let result = text;
    Object.keys(logoUrls).forEach((logoKey) => {
      const regex = new RegExp(`\\{\\{${logoKey}\\}\\}`, "gi");
      result = result.replace(regex, logoUrls[logoKey] || "");
    });
    return result;
  };

  const handleSavePlaceholders = async () => {
    if (!editingTemplate || !activeUserEmail) return;

    try {
      setLoadingTemplates(true);

      // Use edited content if available, otherwise use template content
      const baseSubject = editedHtmlContent?.subject || editingTemplate.subject;
      const baseHtmlBody = editedHtmlContent?.htmlBody || editingTemplate.htmlBody;

      // First replace logo URLs in the template
      let processedSubject = replaceLogoPlaceholders(baseSubject, logoUrls);
      let processedHtmlBody = replaceLogoPlaceholders(baseHtmlBody, logoUrls);

      // Then replace other placeholders
      const updatedSubject = substitutePlaceholders(processedSubject, placeholderValues);
      const updatedHtmlBody = substitutePlaceholders(processedHtmlBody, placeholderValues);

      // Prepare customization data
      const templateId = editingTemplate._id || editingTemplate.id;
      const customizationData = {
        subject: updatedSubject,
        htmlBody: updatedHtmlBody,
        customData: {
          placeholderValues: placeholderValues,
          logoUrls: logoUrls,
        },
      };

      // Save user-specific customization (doesn't update base template)
      await templateAPI.customizeTemplate(templateId, activeUserEmail, customizationData);

      // Reload templates to get updated data
      await loadTemplates();

      // Update selected template if it was the one being edited
      const updatedTemplates = await templateAPI.getTemplates(activeUserEmail);
      const templatesData = Array.isArray(updatedTemplates)
        ? updatedTemplates
        : updatedTemplates.data || updatedTemplates.templates || [];

      const updatedTemplate = templatesData.find((t) => (t._id || t.id) === templateId);
      if (updatedTemplate && selectedTemplate?.id === templateId) {
        setSelectedTemplate({
          ...updatedTemplate,
          id: updatedTemplate._id || updatedTemplate.id,
        });
      }

      setEditingTemplate(null);
      setPlaceholderValues({});
      setLogoUrls({});
      setEditingField(null);
      setEditedHtmlContent(null);

      showMessage(i18n.t("home.templateUpdatedSuccess"), "success");
    } catch (error) {
      // console.error("Error saving template:", error);
      showMessage(apiUtils.formatErrorMessage(error) || "Failed to save template", "error");
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Save template from preview (inline editing)
  const handleSaveFromPreview = async () => {
    if (!selectedTemplate || !activeUserEmail) return;

    try {
      setLoadingTemplates(true);

      // Get the current edited content from preview
      const finalSubject = editedHtmlContent?.subject || selectedTemplate.subject;
      const finalHtmlBody = editedHtmlContent?.htmlBody || selectedTemplate.htmlBody;

      // Process placeholders and logos
      // Note: placeholderValues are no longer used - users edit content directly in preview
      let processedSubject = replaceLogoPlaceholders(finalSubject, logoUrls);
      let processedHtmlBody = replaceLogoPlaceholders(finalHtmlBody, logoUrls);

      // The edited content already has placeholders replaced with user's text, so no need for placeholder substitution
      const templateId = selectedTemplate._id || selectedTemplate.id;
      const customizationData = {
        subject: processedSubject,
        htmlBody: processedHtmlBody,
        customData: {
          logoUrls: logoUrls,
        },
      };

      // Save user-specific customization
      await templateAPI.customizeTemplate(templateId, activeUserEmail, customizationData);

      // Reload templates to show user-specific version
      await loadTemplates();

      // Update selected template to show customized version
      const updatedTemplates = await templateAPI.getTemplates(activeUserEmail);
      const templatesData = Array.isArray(updatedTemplates)
        ? updatedTemplates
        : updatedTemplates.data || updatedTemplates.templates || [];

      const updatedTemplate = templatesData.find((t) => (t._id || t.id) === templateId);
      if (updatedTemplate) {
        // Update selected template with customized version
        const customizedTemplate = {
          ...updatedTemplate,
          id: updatedTemplate._id || updatedTemplate.id,
          isCustomized: true,
        };
        setSelectedTemplate(customizedTemplate);

        // Restore customizations from saved data
        if (updatedTemplate.customData) {
          if (updatedTemplate.customData.logoUrls) {
            setLogoUrls(updatedTemplate.customData.logoUrls);
          }
        }

        // Set editedHtmlContent to the saved customized content so user can continue editing
        setEditedHtmlContent({
          subject: updatedTemplate.subject,
          htmlBody: updatedTemplate.htmlBody,
        });

        // Mark changes as saved since we just saved them
        setChangesSaved(true);
      }

      showMessage(i18n.t("home.templateUpdatedSuccess"), "success");
    } catch (error) {
      // console.error("Error saving template:", error);
      showMessage(apiUtils.formatErrorMessage(error) || "Failed to save template", "error");
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingTemplate(null);
    setPlaceholderValues({});
    setLogoUrls({});
    setEditingField(null);
  };

  const handlePlaceholderChange = (placeholder, value) => {
    setPlaceholderValues((prev) => ({
      ...prev,
      [placeholder]: value,
    }));
  };

  // Awareness message flow handlers
  const handleResetFlow = () => {
    setFlowStep("template");
    setSelectedTemplate(null);
    setSelectedGroups([]);
    setSuccessMessage(null);
    setPlaceholderValues({}); // Clear placeholder values when resetting
    setLogoUrls({}); // Clear logo URLs when resetting
    setManualEmails("");
  };

  const handleTemplateSelect = (template) => {
    // Ensure we're using the template with all its properties
    const templateToUse = {
      ...template,
      id: template._id || template.id,
      subject: template.subject,
      htmlBody: template.htmlBody,
    };

    setSelectedTemplate(templateToUse);

    // Extract logo placeholders to initialize logoUrls
    const logoPlaceholders = [
      ...extractLogoPlaceholders(templateToUse.subject),
      ...extractLogoPlaceholders(templateToUse.htmlBody),
    ];

    // Initialize placeholder values - NO auto-fill, admin must write manually
    const allPlaceholders = [
      ...extractPlaceholders(templateToUse.subject),
      ...extractPlaceholders(templateToUse.htmlBody),
    ];
    const initialPlaceholderValues = {};
    allPlaceholders.forEach((placeholder) => {
      // Don't auto-fill - start with empty string
      initialPlaceholderValues[placeholder] = "";
    });

    // Initialize logo URLs - start empty, admin must fill manually
    const initialLogoUrls = {};
    logoPlaceholders.forEach((logoPlaceholder) => {
      initialLogoUrls[logoPlaceholder] = "";
    });

    // Restore user-specific customizations if they exist (only if template is customized)
    if (templateToUse.isCustomized && templateToUse.customData) {
      if (templateToUse.customData.placeholderValues) {
        // Merge with existing values, but don't auto-fill new ones
        Object.keys(templateToUse.customData.placeholderValues).forEach((key) => {
          initialPlaceholderValues[key] = templateToUse.customData.placeholderValues[key];
        });
      }
      if (templateToUse.customData.logoUrls) {
        Object.assign(initialLogoUrls, templateToUse.customData.logoUrls);
      }
    }

    setPlaceholderValues(initialPlaceholderValues);
    setLogoUrls(initialLogoUrls);

    // If template has customized subject/htmlBody, use those (this is the user's customized version)
    if (templateToUse.isCustomized) {
      // Load the customized content so user can continue editing
      setEditedHtmlContent({
        subject: templateToUse.subject,
        htmlBody: templateToUse.htmlBody,
      });
      // Mark as saved since this is the loaded customized version
      setChangesSaved(true);
    } else {
      // For base template, start with no edited content
      setEditedHtmlContent(null);
      setChangesSaved(true);
    }

    // Preview is shown in the same step, don't move to next step yet
  };

  const handleContinueToGroup = async () => {
    if (!selectedTemplate || !activeUserEmail) return;
    if (loadingTemplates) return;

    // Auto-save any edits from the preview before moving forward
    await handleSaveFromPreview();

    setFlowStep("group");
    // Load groups when moving to group selection
    await loadGroups();
  };

  const loadGroups = async () => {
    try {
      setLoadingGroups(true);
      const groupsData = await graphAPI.getGroups();
      // Filter to show mail-enabled or security groups
      const filteredGroups = groupsData.filter(
        (group) => group.mailEnabled || group.securityEnabled
      );
      setGroups(filteredGroups);
    } catch (error) {
      // console.error("Error loading groups:", error);
      showMessage(i18n.t("home.failedToLoadGroups"), "error");
      // Set empty array on error to prevent UI issues
      setGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  };

  const handleGroupToggle = (group) => {
    setSelectedGroups((prev) => {
      const isSelected = prev.some((g) => g.id === group.id);
      if (isSelected) {
        // Remove group if already selected
        return prev.filter((g) => g.id !== group.id);
      } else {
        // Add group if not selected
        return [...prev, group];
      }
    });
  };

  // Parse manual email addresses from textarea (comma or newline separated)
  const parseManualEmails = (emailText) => {
    if (!emailText || !emailText.trim()) return [];

    return emailText
      .split(/[,\n]/)
      .map((email) => email.trim())
      .filter((email) => email.length > 0 && email.includes("@"));
  };

  const handleContinueFromGroup = () => {
    const parsedEmails = parseManualEmails(manualEmails);

    // Validate: must have either groups or manual emails
    if (selectedGroups.length === 0 && parsedEmails.length === 0) {
      showMessage(i18n.t("home.selectGroupOrEmail"), "error");
      return;
    }

    setFlowStep("confirm");
  };

  const handleSendMessage = async () => {
    try {
      setSendingMessage(true);

      // Get the customized content if available, otherwise use template content
      const finalSubject = editedHtmlContent?.subject || selectedTemplate.subject;
      const finalHtmlBody = editedHtmlContent?.htmlBody || selectedTemplate.htmlBody;

      // Replace logo URLs first
      let processedSubject = replaceLogoPlaceholders(finalSubject, logoUrls);
      let processedHtmlBody = replaceLogoPlaceholders(finalHtmlBody, logoUrls);

      // Substitute placeholders when sending (use defaults for DATE, TIME, YEAR, etc.)
      // Use first group name or comma-separated list if multiple groups
      const groupNames = selectedGroups.map((g) => g.displayName).join(", ");
      const substitutionVars = {
        GROUP_NAME: groupNames || "Group",
      };

      const substitutedSubject = substitutePlaceholders(processedSubject, substitutionVars);
      const substitutedBody = substitutePlaceholders(processedHtmlBody, substitutionVars);

      // Parse manual email addresses
      const parsedManualEmails = parseManualEmails(manualEmails);

      // Get group IDs array
      const groupIds = selectedGroups.map((g) => g.id);

      // Call actual Graph API to send the message
      const result = await graphAPI.sendAwarenessMessage(
        groupIds.length > 0 ? groupIds : null,
        selectedTemplate.id,
        {
          subject: substitutedSubject,
          htmlBody: substitutedBody,
          body: substitutedBody, // Fallback for body
          manualEmails: parsedManualEmails,
        }
      );

      if (result.success) {
        setFlowStep("success");
        const groupNames = selectedGroups.map((g) => g.displayName).join(", ") || "recipients";
        setSuccessMessage(
          result.recipientsCount === result.totalRecipients
            ? i18n.t("home.messageSentTo", {
                count: result.recipientsCount,
                group: groupNames,
              })
            : i18n.t("home.messageSentToPartial", {
                count: result.recipientsCount,
                total: result.totalRecipients,
                group: groupNames,
              })
        );
        // Clear placeholder values, manual emails, and selected groups after successful send
        setPlaceholderValues({});
        setManualEmails("");
        setSelectedGroups([]);
      } else {
        showMessage(result.message || i18n.t("home.failedToSendMessage"), "error");
      }
    } catch (error) {
      // console.error("Error sending message:", error);
      showMessage(error.message || i18n.t("home.failedToSendMessage"), "error");
    } finally {
      setSendingMessage(false);
    }
  };

  // Handle email report on mobile (same flow as commands.js: domain check, send email, ForwardByAddIn, message by boolean)
  const handleEmailReport = async () => {
    if (!reportType) {
      showMessage(i18n.t("messages.selectReportType"), "error");
      return;
    }

    try {
      setReportingEmail(true);

      const item = Office.context.mailbox.item;
      if (!item) {
        showMessage(i18n.t("messages.noEmailSelected"), "error");
        setReportingEmail(false);
        return;
      }

      const userEmail = Office.context.mailbox.userProfile?.emailAddress || "";
      const userDomain = userEmail && userEmail.indexOf("@") >= 0 ? userEmail.split("@")[1].toLowerCase().trim() : "";
      if (!userDomain) {
        showMessage(i18n.t("commands.accessDeniedDescription"), "error");
        setReportingEmail(false);
        return;
      }

      // Same as commands.js: get user settings via domain check
      let userSettings = null;
      try {
        const domainCheckUrl = `${config.BACKEND_URL}/api/domains/check?domain=${encodeURIComponent(userDomain)}`;
        const domainRes = await fetch(domainCheckUrl);
        const domainData = await domainRes.json();
        if (domainData.success && domainData.user) {
          userSettings = domainData.user;
        }
      } catch (e) {
        // console.error("Domain check failed:", e);
      }

      if (!userSettings || !userSettings.forwardEmail) {
        showMessage(i18n.t("messages.forwardEmailNotConfigured"), "error");
        setReportingEmail(false);
        return;
      }

      // Same as spamreporting.js: get tenant, convert item id to REST (for Graph), POST to backend
      let tenantId = null;
      try {
        const tenantRes = await fetch(
          `${config.BACKEND_URL}/api/tenant?domain=${encodeURIComponent(userDomain)}`
        );
        const tenantData = await tenantRes.json();
        if (tenantData.success && tenantData.tenantId) tenantId = tenantData.tenantId;
      } catch (e) {
        // console.error("Tenant fetch failed:", e);
      }

      if (!tenantId) {
        showMessage(i18n.t("commands.messageReportedButForwardingFailed", { errorMessage: i18n.t("commands.forwardingFailedNoTenant") }), "warning");
        setReportingEmail(false);
        return;
      }

      let messageId = item.itemId || "";
      // Convert EWS item id to REST id for Graph API (avoids "this segment is not found" on some clients)
      if (messageId && typeof Office.context.mailbox.convertToRestId === "function") {
        try {
          const restId = Office.context.mailbox.convertToRestId(messageId, Office.MailboxEnums.RestVersion.v2_0);
          if (restId && typeof restId === "string") messageId = restId;
        } catch (e) { /* keep original */ }
      }

      // Map reportType (spam/phishing/suspicious) to SelectedReasonIds: 1=Spam, 2=Phishing, 3=Suspicious (same as web/desktop)
      const selectedReasonIds = reportType === "spam" ? [1] : reportType === "phishing" ? [2] : reportType === "suspicious" ? [3] : [];
      const additionalInformation = reportAdditionalInfo != null ? String(reportAdditionalInfo).trim() : "";

      const payload =
        "tenantId=" + encodeURIComponent(tenantId) +
        "&messageId=" + encodeURIComponent(messageId) +
        "&userPrincipalName=" + encodeURIComponent(userEmail) +
        "&SelectedReasonIds=" + encodeURIComponent(JSON.stringify(selectedReasonIds)) +
        "&AdditionalInformation=" + encodeURIComponent(additionalInformation) +
        // Outlook mobile has no integrated spam-reporting API (event.completed moveItemTo) to move the item,
        // so ask the backend to apply the admin's move/delete setting after a successful report.
        "&applyAllowedActions=true";

      const postRes = await fetch(`${config.BACKEND_URL}/api/send-report-email`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload,
      });

      if (postRes.ok) {
        let itemAction = null;
        try {
          const data = await postRes.json();
          if (data && data.itemAction) itemAction = data.itemAction;
        } catch (_) {}
        const thankYou = i18n.t("commands.thankYouReportingEmail");
        if (itemAction?.success === false) {
          showMessage(`${thankYou} ${i18n.t("messages.reportedEmailNotMoved")}`, "warning");
        } else if (itemAction?.action === "move") {
          showMessage(`${thankYou} ${i18n.t("messages.reportedEmailMovedToJunk")}`, "success");
        } else if (itemAction?.action === "delete") {
          showMessage(`${thankYou} ${i18n.t("messages.reportedEmailMovedToDeletedItems")}`, "success");
        } else {
          showMessage(thankYou, "success");
        }
        setReportType(null);
        setReportAdditionalInfo("");
      } else {
        let errMsg = i18n.t("commands.unknownErrorSendingEmail");
        try {
          const errData = await postRes.json();
          if (errData && errData.error) errMsg = errData.error;
        } catch (_) {}
        showMessage(
          i18n.t("commands.messageReportedButForwardingFailed", { errorMessage: errMsg }),
          "warning"
        );
      }
      setReportingEmail(false);
    } catch (error) {
      // console.error("Error in email report:", error);
      showMessage(i18n.t("messages.failedToReportEmail"), "error");
      setReportingEmail(false);
    }
  };

  // Mobile: always show only the report screen (no access denied, no admin)
  if (isMobile) {
    return (
      <MobileReportScreen
        message={message}
        getMessageBarClass={getMessageBarClass}
        reportType={reportType}
        setReportType={setReportType}
        reportAdditionalInfo={reportAdditionalInfo}
        setReportAdditionalInfo={setReportAdditionalInfo}
        reportingEmail={reportingEmail}
        onEmailReport={handleEmailReport}
      />
    );
  }

  // Access denied / permission error screen (desktop only)
  if (!hasPermission) {
    return (
      <AccessDeniedScreen
        message={message}
        getMessageBarClass={getMessageBarClass}
        isMobile={isMobile}
        permissionError={permissionError}
        activeUserEmail={activeUserEmail}
        reportType={reportType}
        setReportType={setReportType}
        reportAdditionalInfo={reportAdditionalInfo}
        setReportAdditionalInfo={setReportAdditionalInfo}
        reportingEmail={reportingEmail}
        onEmailReport={handleEmailReport}
      />
    );
  }

  // Admin screen (tabs: Settings, Home, Logs)
  if (isAdmin) {
    return (
      <AdminScreen
        message={message}
        getMessageBarClass={getMessageBarClass}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        getTextDirection={getTextDirection}
        activeUserEmail={activeUserEmail}
        adminSettings={adminSettings}
        setAdminSettings={setAdminSettings}
        textareaRef={textareaRef}
        onSaveAdminSettings={handleSaveAdminSettings}
        loading={loading}
        organization={organization}
        loadingLogs={loadingLogs}
        logsError={logsError}
        loadLogs={loadLogs}
        logs={logs}
        setSelectedLog={setSelectedLog}
        selectedLog={selectedLog}
        editingTemplate={editingTemplate}
        flowStep={flowStep}
        setFlowStep={setFlowStep}
        templates={templates}
        loadingTemplates={loadingTemplates}
        selectedTemplate={selectedTemplate}
        setSelectedTemplate={setSelectedTemplate}
        setPlaceholderValues={setPlaceholderValues}
        setEditedHtmlContent={setEditedHtmlContent}
        handleTemplateSelect={handleTemplateSelect}
        handleEditTemplate={handleEditTemplate}
        editedHtmlContent={editedHtmlContent}
        logoUrls={logoUrls}
        setLogoUrls={setLogoUrls}
        setChangesSaved={setChangesSaved}
        previewRef={previewRef}
        handleSaveFromPreview={handleSaveFromPreview}
        handleContinueToGroup={handleContinueToGroup}
        loadingGroups={loadingGroups}
        groups={groups}
        selectedGroups={selectedGroups}
        handleGroupToggle={handleGroupToggle}
        manualEmails={manualEmails}
        setManualEmails={setManualEmails}
        handleContinueFromGroup={handleContinueFromGroup}
        parseManualEmails={parseManualEmails}
        handleSendMessage={handleSendMessage}
        sendingMessage={sendingMessage}
        successMessage={successMessage}
        handleResetFlow={handleResetFlow}
        editingLogoPlaceholder={editingLogoPlaceholder}
        setEditingLogoPlaceholder={setEditingLogoPlaceholder}
        placeholderValues={placeholderValues}
        handlePlaceholderChange={handlePlaceholderChange}
        handleCancelEdit={handleCancelEdit}
        handleSavePlaceholders={handleSavePlaceholders}
        substitutePlaceholders={substitutePlaceholders}
        replaceLogoPlaceholders={replaceLogoPlaceholders}
      />
    );
  }
}
